/**
 * DelegateEditor - Edit delegate details including access, receipt number, dietary requirements
 * Enhanced with tier upgrade and payment plan capabilities
 */
import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, Building2, Briefcase, Receipt, UtensilsCrossed, 
  Home, Ticket, Shield, Save, Loader2, X, AlertTriangle, Coins,
  CreditCard, Calendar, DollarSign, ArrowUpCircle, Clock, QrCode,
  KeyRound, Send, Ban, ShieldCheck
} from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';
import DelegateQRDisplay from './DelegateQRDisplay';

// ────────────────────────────────────────────────────────────────────
// Account actions panel — admin-triggered password reset (mirrors School)
// ────────────────────────────────────────────────────────────────────
const DelegateAccountActions = ({ delegate, eventId }) => {
  const [loading, setLoading] = React.useState(null); // 'send' | 'revoke' | null
  const [status, setStatus] = React.useState(null);
  const hasEmail = !!delegate?.email;

  const fetchStatus = React.useCallback(async () => {
    if (!delegate?.id) return;
    try {
      const r = await api.get(`/admin/events/${eventId}/delegates/${delegate.id}/password-reset-status`);
      setStatus(r.data);
    } catch {
      /* silent — endpoint may not exist on legacy backends */
    }
  }, [delegate?.id, eventId]);

  React.useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const sendReset = async () => {
    if (!hasEmail) {
      toast.error('Delegate has no email on file');
      return;
    }
    setLoading('send');
    try {
      const r = await api.post(`/admin/events/${eventId}/delegates/${delegate.id}/send-password-reset`);
      toast.success(r.data?.message || 'Password reset link sent');
      await fetchStatus();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to send password reset'));
    } finally {
      setLoading(null);
    }
  };

  const revokeReset = async () => {
    if (!confirm('Revoke this delegate\u2019s outstanding password-reset link?')) return;
    setLoading('revoke');
    try {
      await api.post(`/admin/events/${eventId}/delegates/${delegate.id}/revoke-password-reset`);
      toast.success('Reset link revoked');
      await fetchStatus();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to revoke'));
    } finally {
      setLoading(null);
    }
  };

  const activeExpires = status?.has_active_reset && status?.expires_at
    ? new Date(status.expires_at).toLocaleString()
    : null;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-4 bg-slate-50/60">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-blue-100 text-blue-700 mt-0.5">
            <KeyRound className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-slate-900 text-sm">Password Reset</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Send an email containing a secure, single-use link that lets the delegate set a new password. Link is valid for 1 hour and returns them to the Events Portal login on success.
            </p>
            <div className="mt-2 flex items-center gap-2 text-xs">
              {hasEmail ? (
                <Badge variant="outline" className="bg-white">{delegate.email}</Badge>
              ) : (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                  No email on file
                </Badge>
              )}
              {status?.has_active_reset && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="delegate-reset-active-badge">
                  <Clock className="h-3 w-3 mr-1" />
                  Active link · expires {activeExpires}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={sendReset}
            disabled={loading === 'send' || !hasEmail}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="send-password-reset-btn"
          >
            {loading === 'send' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            {status?.has_active_reset ? 'Resend password reset' : 'Send password reset'}
          </Button>
          {status?.has_active_reset && (
            <Button
              onClick={revokeReset}
              disabled={loading === 'revoke'}
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="revoke-password-reset-btn"
            >
              {loading === 'revoke' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Ban className="h-4 w-4 mr-1" />}
              Revoke active link
            </Button>
          )}
          <Button
            onClick={fetchStatus}
            variant="outline"
            size="icon"
            title="Refresh status"
            data-testid="refresh-reset-status"
          >
            <ShieldCheck className="h-4 w-4" />
          </Button>
        </div>

        <p className="text-[11px] text-slate-400 mt-3">
          Every action is written to the audit log with your admin identity, the delegate id, the event id, timestamp and IP address. Administrators cannot see or set a delegate&apos;s password directly.
        </p>
      </div>
    </div>
  );
};

const DelegateEditor = ({ delegate, eventId, open, onClose, onSave }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('basic');
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showPaymentPlanDialog, setShowPaymentPlanDialog] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    gender: '',
    status: 'confirmed',
    tier_id: '',
    receipt_number: '',
    notes: '',
    dietary_requirements: '',
    emergency_contact: '',
    emergency_phone: '',
    // Access permissions
    meal_access: true,
    accommodation_access: false,
    session_access: true,
    lounge_access: false,
    vip_access: false,
  });

  // Upgrade form state
  const [upgradeForm, setUpgradeForm] = useState({
    new_tier_id: '',
    notes: '',
    send_notification: true
  });

  // Payment plan form state
  const [paymentPlanForm, setPaymentPlanForm] = useState({
    total_amount: 0,
    installment_count: 3,
    first_payment_amount: 0,
    frequency_days: 30,
    notes: '',
    send_reminders: true
  });

  // Fetch tiers for dropdown
  const { data: tiers } = useQuery({
    queryKey: ['tiers', eventId],
    queryFn: () => api.get(`/events/${eventId}/tiers`).then(r => r.data),
    enabled: !!eventId,
  });

  // Fetch existing payment plan for delegate
  const { data: existingPaymentPlan } = useQuery({
    queryKey: ['delegate-payment-plan', eventId, delegate?.id],
    queryFn: () => api.get(`/admin/events/${eventId}/delegates/${delegate?.id}/payment-plan`).then(r => r.data).catch(() => null),
    enabled: !!eventId && !!delegate?.id,
  });

  // Load delegate data when dialog opens
  useEffect(() => {
    if (delegate && open) {
      setForm({
        name: delegate.name || '',
        email: delegate.email || '',
        phone: delegate.phone || '',
        company: delegate.company || '',
        title: delegate.title || '',
        gender: delegate.gender || '',
        status: delegate.status || 'confirmed',
        tier_id: delegate.tier_id || '',
        receipt_number: delegate.receipt_number || '',
        notes: delegate.notes || '',
        dietary_requirements: delegate.dietary_requirements || '',
        emergency_contact: delegate.emergency_contact || '',
        emergency_phone: delegate.emergency_phone || '',
        // Access permissions
        meal_access: delegate.access?.meal_access ?? true,
        accommodation_access: delegate.access?.accommodation_access ?? false,
        session_access: delegate.access?.session_access ?? true,
        lounge_access: delegate.access?.lounge_access ?? false,
        vip_access: delegate.access?.vip_access ?? false,
      });
    }
  }, [delegate, open]);

  const updateMutation = useMutation({
    mutationFn: (data) => api.put(`/events/${eventId}/delegates/${delegate.id}`, data),
    onSuccess: () => {
      toast.success('Delegate updated successfully');
      queryClient.invalidateQueries(['delegates', eventId]);
      onSave?.();
      onClose();
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to update delegate');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/events/${eventId}/delegates/${delegate.id}`),
    onSuccess: () => {
      toast.success('Delegate removed');
      queryClient.invalidateQueries(['delegates', eventId]);
      onClose();
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to remove delegate');
    }
  });

  // Tier assignment mutation
  const assignTierMutation = useMutation({
    mutationFn: (data) => api.post(`/admin/events/${eventId}/delegates/${delegate.id}/assign-tier`, {
      tier_id: data.new_tier_id,
      notes: data.notes
    }),
    onSuccess: (response) => {
      toast.success(response.data?.message || 'Tier assigned successfully');
      queryClient.invalidateQueries(['delegates', eventId]);
      setShowUpgradeDialog(false);
      // If there's an amount due, offer to create a payment plan
      if (response.data?.amount_due > 0) {
        setPaymentPlanForm(prev => ({
          ...prev,
          total_amount: response.data.amount_due
        }));
      }
      onSave?.();
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to assign tier');
    }
  });

  // Payment plan creation mutation
  const createPaymentPlanMutation = useMutation({
    mutationFn: (data) => api.post(`/admin/events/${eventId}/delegates/${delegate.id}/payment-plan`, data),
    onSuccess: (response) => {
      toast.success('Payment plan created successfully');
      queryClient.invalidateQueries(['delegates', eventId]);
      queryClient.invalidateQueries(['delegate-payment-plan', eventId, delegate.id]);
      setShowPaymentPlanDialog(false);
      onSave?.();
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to create payment plan');
    }
  });

  // Record payment mutation
  const recordPaymentMutation = useMutation({
    mutationFn: (data) => api.post(`/admin/events/${eventId}/payment-plans/${existingPaymentPlan?.id}/payment`, data),
    onSuccess: () => {
      toast.success('Payment recorded');
      queryClient.invalidateQueries(['delegate-payment-plan', eventId, delegate.id]);
      queryClient.invalidateQueries(['delegates', eventId]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  });

  const handleSave = () => {
    // Only name is required - email is optional
    if (!form.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    // Validate email format only if provided
    if (form.email && form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error('Please enter a valid email address or leave it empty');
      return;
    }
    updateMutation.mutate(form);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to remove this delegate? This action cannot be undone.')) {
      deleteMutation.mutate();
    }
  };

  if (!delegate) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Edit Delegate
          </DialogTitle>
          <DialogDescription>
            Update delegate information, access permissions, and payment details.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 w-full">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="access">Access</TabsTrigger>
            <TabsTrigger value="payment">Payment</TabsTrigger>
            <TabsTrigger value="badge">Badge/QR</TabsTrigger>
            <TabsTrigger value="account" data-testid="delegate-account-tab">Account</TabsTrigger>
            <TabsTrigger value="other">Other</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="John Doe"
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email (optional)</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="john@example.com (optional)"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+1 234 567 8900"
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="company"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="Acme Inc."
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Job Title</Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Software Engineer"
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender">Gender</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="checked_in">Checked In</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="interested">Interested</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tier">Ticket Tier</Label>
                <Select value={form.tier_id || 'none'} onValueChange={(v) => setForm({ ...form, tier_id: v === 'none' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Tier (Free)</SelectItem>
                    {tiers?.map((tier) => (
                      <SelectItem key={tier.id} value={tier.id}>
                        {tier.name} - ${tier.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </TabsContent>

          {/* Access Tab */}
          <TabsContent value="access" className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <h4 className="font-semibold flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Access Permissions
              </h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white rounded border">
                  <div className="flex items-center gap-3">
                    <UtensilsCrossed className="h-5 w-5 text-orange-500" />
                    <div>
                      <p className="font-medium">Meal Access</p>
                      <p className="text-sm text-slate-500">Can access event meals and catering</p>
                    </div>
                  </div>
                  <Switch
                    checked={form.meal_access}
                    onCheckedChange={(checked) => setForm({ ...form, meal_access: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded border">
                  <div className="flex items-center gap-3">
                    <Home className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="font-medium">Accommodation Access</p>
                      <p className="text-sm text-slate-500">Can use event housing/dormitory</p>
                    </div>
                  </div>
                  <Switch
                    checked={form.accommodation_access}
                    onCheckedChange={(checked) => setForm({ ...form, accommodation_access: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded border">
                  <div className="flex items-center gap-3">
                    <Ticket className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="font-medium">Session Access</p>
                      <p className="text-sm text-slate-500">Can attend sessions and workshops</p>
                    </div>
                  </div>
                  <Switch
                    checked={form.session_access}
                    onCheckedChange={(checked) => setForm({ ...form, session_access: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded border">
                  <div className="flex items-center gap-3">
                    <Coins className="h-5 w-5 text-purple-500" />
                    <div>
                      <p className="font-medium">Lounge Access</p>
                      <p className="text-sm text-slate-500">Can use VIP/delegate lounge</p>
                    </div>
                  </div>
                  <Switch
                    checked={form.lounge_access}
                    onCheckedChange={(checked) => setForm({ ...form, lounge_access: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded border">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-amber-500" />
                    <div>
                      <p className="font-medium">VIP Access</p>
                      <p className="text-sm text-slate-500">Full VIP privileges and areas</p>
                    </div>
                  </div>
                  <Switch
                    checked={form.vip_access}
                    onCheckedChange={(checked) => setForm({ ...form, vip_access: checked })}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Payment Tab */}
          <TabsContent value="payment" className="space-y-4 mt-4">
            <div className="space-y-4">
              {/* Tier Assignment Section - Show prominently for interested delegates */}
              {(delegate?.status === 'interested' || !delegate?.tier_id) && (
                <Alert className="border-blue-200 bg-blue-50">
                  <ArrowUpCircle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="flex items-center justify-between">
                    <span className="text-blue-800">
                      This delegate hasn't been assigned a ticket tier yet.
                    </span>
                    <Button 
                      size="sm" 
                      onClick={() => setShowUpgradeDialog(true)}
                      data-testid="assign-tier-btn"
                    >
                      <Ticket className="h-4 w-4 mr-2" />
                      Assign Tier
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Current Tier & Upgrade Option */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Ticket className="h-4 w-4" />
                    Ticket Tier
                  </h4>
                  {delegate?.tier_id && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowUpgradeDialog(true)}
                    >
                      Change Tier
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="text-base px-3 py-1">
                    {delegate?.tier_name || tiers?.find(t => t.id === delegate?.tier_id)?.name || 'No Tier (Interest Only)'}
                  </Badge>
                  {delegate?.tier_price > 0 && (
                    <span className="text-sm text-slate-500">
                      ${delegate.tier_price?.toFixed(2)}
                    </span>
                  )}
                </div>
                {delegate?.amount_due > 0 && (
                  <div className="flex items-center gap-2 text-amber-600">
                    <DollarSign className="h-4 w-4" />
                    <span className="font-medium">Amount Due: ${delegate.amount_due?.toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* Payment Plan Section */}
              {delegate?.amount_due > 0 && !existingPaymentPlan && (
                <Alert className="border-amber-200 bg-amber-50">
                  <CreditCard className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="flex items-center justify-between">
                    <span className="text-amber-800">
                      Delegate has outstanding balance. Create a payment plan?
                    </span>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setPaymentPlanForm(prev => ({
                          ...prev,
                          total_amount: delegate.amount_due
                        }));
                        setShowPaymentPlanDialog(true);
                      }}
                      data-testid="create-payment-plan-btn"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Create Payment Plan
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Existing Payment Plan Display */}
              {existingPaymentPlan && (
                <div className="p-4 border rounded-lg space-y-3 bg-white">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-green-600" />
                      Active Payment Plan
                    </h4>
                    <Badge variant={existingPaymentPlan.status === 'completed' ? 'default' : 'secondary'}>
                      {existingPaymentPlan.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Total:</span>{' '}
                      <span className="font-medium">${existingPaymentPlan.total_amount?.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Paid:</span>{' '}
                      <span className="font-medium text-green-600">${existingPaymentPlan.amount_paid?.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Remaining:</span>{' '}
                      <span className="font-medium text-amber-600">
                        ${(existingPaymentPlan.total_amount - existingPaymentPlan.amount_paid)?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {/* Installments */}
                  <div className="space-y-2 mt-3">
                    <Label className="text-xs text-slate-500">Installments</Label>
                    {existingPaymentPlan.installments?.map((inst, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-center justify-between p-2 rounded text-sm ${
                          inst.paid ? 'bg-green-50' : inst.overdue ? 'bg-red-50' : 'bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">#{inst.number}</span>
                          <span className="text-slate-500">${inst.amount?.toFixed(2)}</span>
                          <span className="text-xs text-slate-400">
                            Due: {new Date(inst.due_date).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {inst.paid ? (
                            <Badge variant="default" className="bg-green-600">Paid</Badge>
                          ) : inst.overdue ? (
                            <>
                              <Badge variant="destructive">Overdue</Badge>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => recordPaymentMutation.mutate({
                                  amount: inst.amount,
                                  installment_number: inst.number,
                                  payment_method: 'cash'
                                })}
                                disabled={recordPaymentMutation.isPending}
                              >
                                Mark Paid
                              </Button>
                            </>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => recordPaymentMutation.mutate({
                                amount: inst.amount,
                                installment_number: inst.number,
                                payment_method: 'cash'
                              })}
                              disabled={recordPaymentMutation.isPending}
                            >
                              Mark Paid
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="receipt_number">Receipt/Invoice Number</Label>
                <div className="relative">
                  <Receipt className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="receipt_number"
                    value={form.receipt_number}
                    onChange={(e) => setForm({ ...form, receipt_number: e.target.value })}
                    placeholder="INV-2024-001 (optional)"
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-slate-500">Optional reference number for payment tracking</p>
              </div>

              {/* Show existing payment info if available */}
              {delegate?.payment && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <h4 className="font-semibold text-sm">Payment Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">Method:</span>{' '}
                      <Badge variant="outline">{delegate.payment.method}</Badge>
                    </div>
                    <div>
                      <span className="text-slate-500">Status:</span>{' '}
                      <Badge variant={delegate.payment.status === 'paid' ? 'default' : 'secondary'}>
                        {delegate.payment.status}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-slate-500">Amount Paid:</span>{' '}
                      ${delegate.payment.amount_paid?.toFixed(2) || '0.00'}
                    </div>
                    <div>
                      <span className="text-slate-500">Total Due:</span>{' '}
                      ${delegate.payment.total_due?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="notes">Payment Notes</Label>
                <Textarea
                  id="notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any notes about payment or registration..."
                  rows={3}
                />
              </div>
            </div>
          </TabsContent>

          {/* Badge/QR Tab */}
          <TabsContent value="badge" className="space-y-4 mt-4">
            <div className="flex justify-center">
              <DelegateQRDisplay 
                delegate={delegate}
                eventId={eventId}
                eventName="Event"
              />
            </div>
          </TabsContent>

          {/* Account Tab — Password reset + audit */}
          <TabsContent value="account" className="space-y-4 mt-4">
            <DelegateAccountActions delegate={delegate} eventId={eventId} />
          </TabsContent>

          {/* Other Tab */}
          <TabsContent value="other" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dietary">Dietary Requirements</Label>
                <Textarea
                  id="dietary"
                  value={form.dietary_requirements}
                  onChange={(e) => setForm({ ...form, dietary_requirements: e.target.value })}
                  placeholder="Vegetarian, vegan, allergies, etc."
                  rows={2}
                />
              </div>

              <Separator />

              <h4 className="font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Emergency Contact
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact">Contact Name</Label>
                  <Input
                    id="emergency_contact"
                    value={form.emergency_contact}
                    onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })}
                    placeholder="Jane Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_phone">Contact Phone</Label>
                  <Input
                    id="emergency_phone"
                    value={form.emergency_phone}
                    onChange={(e) => setForm({ ...form, emergency_phone: e.target.value })}
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6 flex justify-between">
          <Button 
            variant="destructive" 
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <X className="h-4 w-4 mr-2" />}
            Remove Delegate
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={updateMutation.isPending || !form.name || !form.email}
            >
              {updateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Tier Assignment Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              Assign Ticket Tier
            </DialogTitle>
            <DialogDescription>
              Assign or change the ticket tier for {delegate?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Tier</Label>
              <Select 
                value={upgradeForm.new_tier_id} 
                onValueChange={(v) => setUpgradeForm({ ...upgradeForm, new_tier_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a tier" />
                </SelectTrigger>
                <SelectContent>
                  {tiers?.map((tier) => (
                    <SelectItem key={tier.id} value={tier.id}>
                      <div className="flex justify-between items-center w-full gap-4">
                        <span>{tier.name}</span>
                        <span className="text-slate-500">${tier.price}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {upgradeForm.new_tier_id && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Tier Price:</span>
                  <span className="font-medium">
                    ${tiers?.find(t => t.id === upgradeForm.new_tier_id)?.price?.toFixed(2) || '0.00'}
                  </span>
                </div>
                {delegate?.amount_paid > 0 && (
                  <div className="flex justify-between text-sm text-green-600 mt-1">
                    <span>Already Paid:</span>
                    <span>-${delegate.amount_paid?.toFixed(2)}</span>
                  </div>
                )}
                <Separator className="my-2" />
                <div className="flex justify-between font-semibold">
                  <span>Amount Due:</span>
                  <span>
                    ${Math.max(0, (tiers?.find(t => t.id === upgradeForm.new_tier_id)?.price || 0) - (delegate?.amount_paid || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={upgradeForm.notes}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, notes: e.target.value })}
                placeholder="Any notes about this tier assignment..."
                rows={2}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="send_notification"
                checked={upgradeForm.send_notification}
                onCheckedChange={(checked) => setUpgradeForm({ ...upgradeForm, send_notification: checked })}
              />
              <Label htmlFor="send_notification" className="text-sm">
                Send notification email to delegate
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgradeDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => assignTierMutation.mutate(upgradeForm)}
              disabled={!upgradeForm.new_tier_id || assignTierMutation.isPending}
            >
              {assignTierMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Ticket className="h-4 w-4 mr-2" />
              )}
              Assign Tier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Plan Dialog */}
      <Dialog open={showPaymentPlanDialog} onOpenChange={setShowPaymentPlanDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Create Payment Plan
            </DialogTitle>
            <DialogDescription>
              Set up installment payments for {delegate?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Outstanding Balance:</span>
                <span className="font-semibold">${delegate?.amount_due?.toFixed(2) || '0.00'}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total Amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    value={paymentPlanForm.total_amount}
                    onChange={(e) => setPaymentPlanForm({ ...paymentPlanForm, total_amount: parseFloat(e.target.value) || 0 })}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Number of Installments</Label>
                <Select 
                  value={paymentPlanForm.installment_count.toString()} 
                  onValueChange={(v) => setPaymentPlanForm({ ...paymentPlanForm, installment_count: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 installments</SelectItem>
                    <SelectItem value="3">3 installments</SelectItem>
                    <SelectItem value="4">4 installments</SelectItem>
                    <SelectItem value="6">6 installments</SelectItem>
                    <SelectItem value="12">12 installments</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Payment</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    value={paymentPlanForm.first_payment_amount}
                    onChange={(e) => setPaymentPlanForm({ ...paymentPlanForm, first_payment_amount: parseFloat(e.target.value) || 0 })}
                    placeholder="Optional"
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-slate-500">Leave 0 for equal payments</p>
              </div>
              <div className="space-y-2">
                <Label>Payment Frequency</Label>
                <Select 
                  value={paymentPlanForm.frequency_days.toString()} 
                  onValueChange={(v) => setPaymentPlanForm({ ...paymentPlanForm, frequency_days: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Weekly</SelectItem>
                    <SelectItem value="14">Bi-weekly</SelectItem>
                    <SelectItem value="30">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview */}
            {paymentPlanForm.total_amount > 0 && (
              <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                <Label className="text-xs text-slate-500">Payment Preview</Label>
                <div className="text-sm space-y-1">
                  {Array.from({ length: paymentPlanForm.installment_count }, (_, i) => {
                    const firstAmt = paymentPlanForm.first_payment_amount || (paymentPlanForm.total_amount / paymentPlanForm.installment_count);
                    const remaining = paymentPlanForm.total_amount - (paymentPlanForm.first_payment_amount || 0);
                    const regularAmt = paymentPlanForm.first_payment_amount 
                      ? remaining / (paymentPlanForm.installment_count - 1)
                      : paymentPlanForm.total_amount / paymentPlanForm.installment_count;
                    const amt = i === 0 ? firstAmt : regularAmt;
                    return (
                      <div key={i} className="flex justify-between">
                        <span>Payment {i + 1}:</span>
                        <span className="font-medium">${amt.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={paymentPlanForm.notes}
                onChange={(e) => setPaymentPlanForm({ ...paymentPlanForm, notes: e.target.value })}
                placeholder="Any notes about this payment plan..."
                rows={2}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="send_reminders"
                checked={paymentPlanForm.send_reminders}
                onCheckedChange={(checked) => setPaymentPlanForm({ ...paymentPlanForm, send_reminders: checked })}
              />
              <Label htmlFor="send_reminders" className="text-sm">
                Send payment reminders before due dates
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentPlanDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => createPaymentPlanMutation.mutate(paymentPlanForm)}
              disabled={paymentPlanForm.total_amount <= 0 || createPaymentPlanMutation.isPending}
            >
              {createPaymentPlanMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Calendar className="h-4 w-4 mr-2" />
              )}
              Create Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default DelegateEditor;
